import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth';
import { firstValueFrom, map } from 'rxjs';
import { ProviderService } from '../services/provider/provider.service';
import { UserStore } from '../store/user/user.store';
import { ClinicService } from '../services/clinic/clinic.service';

function normalizeDate(value: any): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString().slice(0, 10);
}

function isExpired(value: any): boolean {
  const normalized = normalizeDate(value);

  if (!normalized) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiration = new Date(`${normalized}T00:00:00`);
  expiration.setHours(0, 0, 0, 0);

  // The expiration date itself counts as expired.
  return expiration <= today;
}

function getLicenceExpireDate(provider: any): string | null {
  return normalizeDate(
    provider?.license_expire_date ??
    provider?.licence_expire_date ??
    provider?.license_expiration_date ??
    provider?.licence_expiration_date ??
    provider?.license_expiry_date ??
    provider?.licence_expiry_date ??
    provider?.primary_provider_license_expire_date ??
    provider?.primary_provider_licence_expire_date ??
    null
  );
}

function getInsuranceExpireDate(provider: any): string | null {
  return normalizeDate(
    provider?.insurance_expire_date ??
    provider?.malpractice_expire_date ??
    provider?.insurance_expiration_date ??
    provider?.malpractice_expiration_date ??
    provider?.insurance_expiry_date ??
    provider?.malpractice_expiry_date ??
    provider?.primary_provider_insurance_expire_date ??
    provider?.primary_provider_malpractice_expire_date ??
    null
  );
}

function hasExpiredRequiredDocuments(provider: any): boolean {
  return isExpired(getLicenceExpireDate(provider)) || isExpired(getInsuranceExpireDate(provider));
}

function extractProviderList(response: any): any[] {
  if (Array.isArray(response)) {
    return response;
  }

  const providers = response?.providers ?? response?.result ?? response?.data ?? response?.items ?? [];
  return Array.isArray(providers) ? providers : [];
}

async function getClinicProviderForExpiryCheck(
  providerService: ProviderService,
  provider: any
): Promise<any> {
  const clinicId = Number(provider?.clinic_id ?? 0);
  const providerId = Number(provider?.provider_id ?? provider?.id ?? 0);

  if (!clinicId || !providerId) {
    return provider;
  }

  try {
    const response = await firstValueFrom(
      providerService.getAllProidersByClinic(clinicId).pipe(
        map((value: any) => value)
      )
    );

    const clinicProviders = extractProviderList(response);
    const matchedProvider = clinicProviders.find((clinicProvider: any) =>
      Number(clinicProvider?.id ?? 0) === providerId ||
      Number(clinicProvider?.provider_id ?? 0) === providerId ||
      (!!provider?.email_address && clinicProvider?.email_address === provider.email_address) ||
      (!!provider?.npi && clinicProvider?.npi === provider.npi)
    );

    return matchedProvider ? { ...provider, ...matchedProvider } : provider;
  } catch (error) {
    console.error('Failed to load clinic providers for expiry-date authorization check:', error);
    return provider;
  }
}

function setUserFromProvider(userStore: any, provider: any, expiryProvider: any, userGroups: string[]): boolean {
  const licenceExpireDate = getLicenceExpireDate(expiryProvider);
  const insuranceExpireDate = getInsuranceExpireDate(expiryProvider);
  const hasExpiredDocuments = hasExpiredRequiredDocuments(expiryProvider);

  userStore.setParams({
    id: provider?.id ?? expiryProvider?.id ?? 0,
    active: provider?.active ?? expiryProvider?.active ?? false,
    clinic_id: provider?.clinic_id ?? expiryProvider?.clinic_id ?? 0,
    provider_id: provider?.provider_id ?? expiryProvider?.id ?? 0,
    onboarding_status: provider?.onboarding_status ?? expiryProvider?.onboarding_status ?? 0,
    user_group: userGroups,
    licence_expire_date: licenceExpireDate,
    license_expire_date: licenceExpireDate,
    insurance_expire_date: insuranceExpireDate,
    has_expired_documents: hasExpiredDocuments
  });

  return hasExpiredDocuments;
}

export const postAuthGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const clinicService = inject(ClinicService);
  const providerService = inject(ProviderService);
  const userStore = inject(UserStore);

  try {
    const decoded = authService.getDecodedToken();
    const userGroups: string[] = Array.isArray(decoded?.['cognito:groups'])
      ? decoded['cognito:groups']
      : [];

    if (userGroups.includes('admin-provider') || userGroups.includes('provider')) {
      const provider = await firstValueFrom(
        providerService.getActiveProvider().pipe(
          map((response: any) => response)
        )
      );

      const providerForExpiryCheck = await getClinicProviderForExpiryCheck(providerService, provider);
      const hasExpiredDocuments = setUserFromProvider(userStore, provider, providerForExpiryCheck, userGroups);

      if (!provider?.active) {
        return router.createUrlTree(['/onboarding']);
      }

      if (hasExpiredDocuments) {
        return router.createUrlTree(['/profile/providers']);
      }

      return router.createUrlTree(['/profile']);
    }

    if (userGroups.includes('admin-clinic')) {
      const admin = await firstValueFrom(
        clinicService.getClinicAdminUser().pipe(
          map((response: any) => response)
        )
      );

      const primaryProvider = {
        ...admin,
        id: admin?.primary_provider_id,
        provider_id: admin?.primary_provider_id,
        active: admin?.active,
        onboarding_status: admin?.primary_provider_onboarding_status
      };

      const providerForExpiryCheck = await getClinicProviderForExpiryCheck(providerService, primaryProvider);
      const hasExpiredDocuments = setUserFromProvider(userStore, primaryProvider, providerForExpiryCheck, userGroups);

      if (!admin?.active) {
        return router.createUrlTree(['/not-authorized']);
      }

      if (hasExpiredDocuments) {
        return router.createUrlTree(['/profile/providers']);
      }

      return router.createUrlTree(['/profile']);
    }

    if (userGroups.includes('admin')) {
      return router.createUrlTree(['/admin']);
    }

    if (userGroups.includes('member')) {
      return router.createUrlTree(['/member']);
    }

    return router.createUrlTree(['/login']);
  } catch (error) {
    console.error('Post-auth guard error:', error);
    return router.createUrlTree(['/login']);
  }
};
